/**
 * The voice abstraction both apps program against (README §24).
 *
 * Deliberately free of any Agora type: the guest web app implements it with
 * `agora-rtc-sdk-ng` and the receptionist app with `react-native-agora`, and no
 * React component ever touches either SDK directly. Swapping the RTC provider
 * means writing one new implementation, not editing screens.
 */

export interface VoiceJoinConfig {
  appId: string;
  channelName: string;
  token: string;
  uid: number;
}

/** Mirrors the connection lifecycle both SDKs report (README §36). */
export const VOICE_CONNECTION_STATE = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  RECONNECTING: 'RECONNECTING',
  FAILED: 'FAILED',
} as const;

export type VoiceConnectionState =
  (typeof VOICE_CONNECTION_STATE)[keyof typeof VOICE_CONNECTION_STATE];

export interface VoiceCallHandlers {
  /** The other party joined and their audio is playing. */
  onRemoteJoined?: (uid: number) => void;
  /** The other party left the channel. */
  onRemoteLeft?: (uid: number) => void;
  onConnectionStateChanged?: (state: VoiceConnectionState) => void;
  /**
   * The token is close to expiry. The app fetches a fresh one from
   * `getAgoraCredentials` and passes it to `renewToken`.
   */
  onTokenWillExpire?: () => void;
  onError?: (error: unknown) => void;
}

export interface VoiceCallService {
  initialize(handlers?: VoiceCallHandlers): Promise<void>;
  join(config: VoiceJoinConfig): Promise<void>;
  leave(): Promise<void>;
  mute(): Promise<void>;
  unmute(): Promise<void>;
  renewToken(token: string): Promise<void>;
  isMuted(): boolean;
  destroy(): Promise<void>;
}
